import { appLog } from "./helpers";
import { Scanner, ScannerData } from "./types";

type SerialReader = ReadableStreamDefaultReader<string>;
type NavigatorWithSerial = Navigator & { serial?: Serial };

type SerialLoopCleanup = ReturnType<typeof setTimeout> | null;

type ReadResult = {
  value?: string;
  done?: boolean;
};

const SYMBOL_VENDOR_ID = 0x05e0; // Symbol Technologies / Zebra
const DEFAULT_BAUD_RATE = 9600;
const DEVICE_LABEL = "Symbol Bar Code Scanner";
const SERIAL_IDLE_FLUSH_MS = 120;

const formatId = (id?: number) => (typeof id === "number" ? `0x${id.toString(16).padStart(4, "0")}` : "unknown");

const describePortInfo = (info: SerialPortInfo) => {
  const vendor = formatId(info.usbVendorId);
  const product = formatId(info.usbProductId);
  return `vendor=${vendor} product=${product}`;
};

const gestureEvents: Array<keyof WindowEventMap> = ["pointerdown", "mousedown", "keydown", "keyup", "touchstart", "touchend"];

export class WebSerialScanner implements Scanner {
  public readonly friendlyName = DEVICE_LABEL;

  private port: SerialPort | null = null;
  private reader: SerialReader | null = null;
  private readLoopPromise: Promise<void> | null = null;
  private reading = false;
  private teardownController = new AbortController();
  private buffer = "";
  private onData: ((data: ScannerData) => void) | null = null;
  private permissionRequestPromise: Promise<SerialPort | null> | null = null;
  private idleFlushTimer: SerialLoopCleanup = null;

  constructor(private readonly baudRate: number = DEFAULT_BAUD_RATE) {}

  private async getOrRequestPort(): Promise<SerialPort> {
    const nav = typeof navigator !== "undefined" ? (navigator as NavigatorWithSerial) : undefined;
    if (!nav) {
      throw new Error("navigator is undefined");
    }

    appLog(`[WebSerialScanner] navigator.serial present=${!!nav.serial}`, true);

    const serial = nav.serial;
    if (!serial) {
      throw new Error("Web Serial API is not available");
    }

    if (this.port) {
      return this.port;
    }

    appLog("[WebSerialScanner] calling getPorts", true);
    const ports = await serial.getPorts();
    const portDescriptions = ports.map((port) => describePortInfo(port.getInfo()));
    if (portDescriptions.length > 0) {
      appLog(`[WebSerialScanner] available ports: ${portDescriptions.join(", ")}`, true);
    } else {
      appLog("[WebSerialScanner] no previously granted ports", true);
    }

    let matchingPort = ports.find((port: SerialPort) => {
      const info = port.getInfo();
      return info.usbVendorId === SYMBOL_VENDOR_ID;
    });

    if (matchingPort) {
      const info = describePortInfo(matchingPort.getInfo());
      appLog(`[WebSerialScanner] reusing existing port ${info}`, true);
      this.port = matchingPort;
      return matchingPort;
    }

    try {
      appLog("[WebSerialScanner] no vendor match, requesting user selection", true);
      const requested = await this.requestPortWithUserGesture(serial);
      if (!requested) {
        throw new Error("User dismissed Web Serial permission prompt");
      }
      matchingPort = requested;
      this.port = requested;
      return requested;
    } catch (error) {
      appLog(
        `[WebSerialScanner] requestPort failed: ${(error as Error)?.message ?? "unknown"}`,
        true,
        "error",
        error as Error
      );
      throw error;
    }
  }

  private async ensurePortOpen(): Promise<void> {
    const port = await this.getOrRequestPort();

    if (port.readable && port.writable) {
      return;
    }

    try {
      await port.open({ baudRate: this.baudRate });
      appLog(`[WebSerialScanner] Port opened at ${this.baudRate} baud`, true);
    } catch (error) {
      appLog(
        `[WebSerialScanner] Failed to open port: ${(error as Error)?.message ?? "unknown"}`,
        true,
        "error",
        error as Error
      );
      throw error;
    }
  }

  private async startReading(): Promise<void> {
    if (!this.port?.readable) {
      throw new Error("Serial port is not readable");
    }

    if (this.reading) {
      return;
    }

    const textDecoder = new TextDecoderStream();
    const reader = textDecoder.readable.getReader();

    this.reading = true;
    this.reader = reader;
    this.buffer = "";
    this.clearIdleTimer();

    this.teardownController.abort();
    this.teardownController = new AbortController();

    const streamClosedPromise = this.port.readable.pipeTo(textDecoder.writable, {
      signal: this.teardownController.signal,
    });

    this.readLoopPromise = (async () => {
      try {
        while (this.reading) {
          const { value, done } = (await reader.read()) as ReadResult;
          if (done) break;
          if (value) {
            this.handleIncomingChunk(value);
          }
        }
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          appLog(
            `[WebSerialScanner] Read loop error: ${(error as Error)?.message ?? "unknown"}`,
            true,
            "error",
            error as Error
          );
        }
      } finally {
        reader.releaseLock();
        await streamClosedPromise.catch(() => {});
        this.reading = false;
        this.reader = null;
        this.readLoopPromise = null;
        this.flushBuffer();
      }
    })();
  }

  private handleIncomingChunk(chunk: string) {
    this.buffer += chunk;

    let separatorIndex: number;
    while ((separatorIndex = this.buffer.search(/[\r\n]/)) !== -1) {
      const scan = this.buffer.slice(0, separatorIndex);
      this.buffer = this.buffer.slice(separatorIndex + 1);
      const trimmed = scan.trim();
      if (trimmed.length > 0) {
        this.emitData(trimmed);
      }
    }

    this.resetIdleTimer();
  }

  private emitData(data: string) {
    if (!this.onData) return;
    appLog(`[WebSerialScanner] scan detected: ${data}`, true, "info", { data });
    try {
      this.onData({ data });
    } catch (error) {
      appLog(
        `[WebSerialScanner] Failed to emit data: ${(error as Error)?.message ?? "unknown"}`,
        true,
        "error",
        error as Error
      );
    }
  }

  async enable(options: { autoRead: boolean }, callback: (data: ScannerData) => void) {
    this.onData = callback;
    await this.ensurePortOpen();
    if (options.autoRead) {
      await this.start();
    }
  }

  async start() {
    await this.ensurePortOpen();
    await this.startReading();
  }

  async stop() {
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          appLog(
            `[WebSerialScanner] stop cancel error: ${(error as Error)?.message ?? "unknown"}`,
            true,
            "error",
            error as Error
          );
        }
      }
    }

    this.teardownController.abort();

    if (this.readLoopPromise) {
      await this.readLoopPromise.catch(() => {});
    }

    this.reading = false;
  }

  async disable() {
    await this.stop();

    if (this.port) {
      try {
        await this.port.close();
        appLog("[WebSerialScanner] Port closed", true);
      } catch (error) {
        appLog(
          `[WebSerialScanner] Failed to close port: ${(error as Error)?.message ?? "unknown"}`,
          true,
          "error",
          error as Error
        );
      }
    }

    this.port = null;
    this.onData = null;
    this.buffer = "";
    this.clearIdleTimer();
  }

  private requestPortWithUserGesture(serial: Serial): Promise<SerialPort | null> {
    if (this.permissionRequestPromise) {
      appLog("[WebSerialScanner] returning pending permission promise", true);
      return this.permissionRequestPromise;
    }

    if (typeof window === "undefined") {
      return Promise.reject(new Error("window is undefined"));
    }

    this.permissionRequestPromise = new Promise((resolve, reject) => {
      let resolved = false;

      const cleanup = () => {
        appLog("[WebSerialScanner] cleaning up gesture listeners", true);
        gestureEvents.forEach((eventName) => window.removeEventListener(eventName, handleGesture as EventListener));
      };

      const finalize = () => {
        cleanup();
        this.permissionRequestPromise = null;
      };

      const handleGesture = async (event: Event) => {
        appLog(`[WebSerialScanner] gesture candidate: ${event.type}`, true);
        if (resolved) return;
        resolved = true;
        appLog(`[WebSerialScanner] user gesture detected via ${event.type}`, true);
        cleanup();
        try {
          appLog("[WebSerialScanner] calling serial.requestPort", true);
          const requested = await serial.requestPort({
            filters: [
              {
                usbVendorId: SYMBOL_VENDOR_ID,
              },
            ],
          });
          this.port = requested;
          appLog(`[WebSerialScanner] user selected port ${describePortInfo(requested.getInfo())}`, true);
          resolve(requested);
        } catch (error) {
          appLog(
            `[WebSerialScanner] user dismissed port selection: ${(error as Error)?.message ?? "unknown"}`,
            true,
            "error",
            error as Error
          );
          reject(error as Error);
        } finally {
          appLog("[WebSerialScanner] finalize permission request", true);
          finalize();
        }
      };

      gestureEvents.forEach((eventName) => {
        window.addEventListener(eventName, handleGesture as EventListener, { once: true });
        appLog(`[WebSerialScanner] attaching gesture listener for ${eventName}`, true);
      });

      appLog("[WebSerialScanner] waiting for user interaction to request port", true);
    });

    return this.permissionRequestPromise;
  }

  private resetIdleTimer() {
    this.clearIdleTimer();
    if (this.buffer.length === 0) {
      return;
    }
    this.idleFlushTimer = setTimeout(() => {
      this.flushBuffer();
    }, SERIAL_IDLE_FLUSH_MS);
  }

  private clearIdleTimer() {
    if (this.idleFlushTimer) {
      clearTimeout(this.idleFlushTimer);
      this.idleFlushTimer = null;
    }
  }

  private flushBuffer() {
    if (!this.buffer) {
      return;
    }
    const trimmed = this.buffer.trim();
    this.buffer = "";
    this.clearIdleTimer();
    if (trimmed.length > 0) {
      this.emitData(trimmed);
    }
  }
}

export const canUseWebSerialScanner = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const isChrome = /Chrome\/\d+/.test(navigator.userAgent) && !/Edg\//.test(navigator.userAgent);
  return isChrome && "serial" in navigator;
};

export const createWebSerialScanner = () => new WebSerialScanner();

export const hasGrantedWebSerialAccess = async () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  const serial = (navigator as NavigatorWithSerial).serial;
  if (!serial) {
    return false;
  }
  const ports = await serial.getPorts();
  return ports.length > 0;
};

export const WEB_SERIAL_SCANNER_FRIENDLY_NAME = DEVICE_LABEL;

